import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import {
  DateRangePicker,
  Input,
  Loader,
  Modal,
  SelectPicker,
  Uploader,
} from "rsuite";
import { Promotion } from "../../../models/sales/promotion";
import { Form, Button } from "rsuite";
import { errorToast, successToast } from "../../../utils/helper-ui";
import { BsCamera, BsImage } from "react-icons/bs";
import { asyncLocalStorage } from "../../../utils/storage";
import { Company } from "../../../models/company/company";
import { getCompanies } from "../../../services/company/CompanyApi";

import { Editor } from "@tinymce/tinymce-react";
import { useRealm } from "@mdxeditor/editor";
import { FileModel } from "../../../models/file/file";
import { TrashIcon } from "@heroicons/react/24/outline";
import moment from "moment";
interface PromotionFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Promotion) => void;
  promotion?: Promotion;
}

const PromotionForm: FC<PromotionFormProps> = ({
  open,
  onClose,
  onSubmit,
  promotion,
}) => {
  const editorRef = useRef<React.MutableRefObject<null>>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | null | undefined
  >(null);
  const [editorContent, setEditorContent] = useState("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [files, setFiles] = useState<FileModel[]>([]);
  const [data, setData] = useState<Promotion>({
    id: "",
    name: "",
    description: "",
  });
  const [uploading, setUploading] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    asyncLocalStorage.getItem("token").then((token) => {
      setToken(token);
    });
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ font: [] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          ["blockquote", "code-block"],
          [{ list: "ordered" }, { list: "bullet" }],

          [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
          [{ direction: "rtl" }],
          [{ size: ["small", false, "large", "huge"] }],
          ["link", "image", "video"],
          ["clean"],
        ],

        history: {
          delay: 500,
          maxStack: 100,
          userOnly: true,
        },
      },
    }),
    []
  );

  const handleEditorChange = (content: any, editor: any) => {
    setEditorContent(content);
  };

  useEffect(() => {
    setData(
      promotion || {
        id: "",
        name: "",
        description: "",
      }
    );

    if (promotion) {
      setFiles(promotion?.images ?? []);
    }
  }, [promotion]);
  return (
    <Modal size={"lg"} open={open} onClose={onClose}>
      <Modal.Header>
        <Modal.Title>Promotion Form</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form className="w-full" layout="horizontal" fluid>
          <div className="form-group">
            <label>Title</label>
            <Input
              name="name"
              placeholder="e.g. Promotion for New Year"
              value={data.name}
              onChange={(value) => setData({ ...data, name: value })}
            />
          </div>
          <div className="form-group">
            <label>Type</label>
            <div className="input ">
              <SelectPicker
                block
                data={[
                  { label: "Discount", value: "DISCOUNT" },
                  { label: "Coupon", value: "COUPON" },
                  { label: "Cashback", value: "CASHBACK" },
                  { label: "Free Shipping", value: "FREE_SHIPPING" },
                ]}
                value={data.type}
                onChange={(val) => {
                  setData({ ...data, type: val! });
                }}
                placeholder="Select a Promotion Type"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Effective</label>
            <div className="input ">
              <DateRangePicker
                className="w-full"
                format="dd MMM yyyy"
                shouldDisableDate={(date) =>
                  date < moment().subtract(1, "days").toDate()
                }
                value={
                  data?.start_date && data?.end_date
                    ? [
                        moment(data?.start_date).toDate(),
                        moment(data?.end_date).toDate(),
                      ]
                    : null
                }
                onChange={(date) => {
                  setData({
                    ...data,
                    start_date: date![0].toISOString(),
                    end_date: date![1].toISOString(),
                  });
                }}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <div className="input min-h-[300px]">
              <Editor
                apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                // onInit={(_evt, editor) => (editorRef!.current = editor)}
                initialValue={data.description ?? ""}
                init={{
                  height: 500,
                  menubar: false,
                  plugins: [
                    "advlist",
                    "autolink",
                    "lists",
                    "link",
                    "image",
                    "charmap",
                    "preview",
                    "anchor",
                    "searchreplace",
                    "visualblocks",
                    "code",
                    "fullscreen",
                    "insertdatetime",
                    "media",
                    "table",
                    "code",
                    "help",
                    "wordcount",
                  ],
                  toolbar:
                    "undo redo | blocks | " +
                    "bold italic forecolor | alignleft aligncenter " +
                    "alignright alignjustify | bullist numlist outdent indent | " +
                    "removeformat | help",
                  content_style:
                    "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                }}
                onEditorChange={handleEditorChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Images</label>
            <div className="input">
              <Uploader
                fileListVisible={false}
                listType="picture"
                accept="image/*"
                action={process.env.REACT_APP_API_URL + "/v1/file/upload"}
                onUpload={(file) => {
                  setUploading(true);
                }}
                className="w-full"
                headers={{
                  Authorization: `Bearer ${token}`,
                }}
                onSuccess={(response, file) => {
                  setUploading(false);
                  successToast("File uploaded successfully");
                  setFiles((val) => {
                    return [...val, response.file];
                  });
                }}
                onError={() => {
                  setUploading(false);
                  errorToast("Upload failed");
                }}
              >
                <div
                  style={{
                    height: 200,
                    width: "100%",
                    padding: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {uploading && <Loader backdrop center />}
                  <span>Click or Drag files to this area to upload</span>
                </div>
              </Uploader>
              <div className="flex flex-row gap-2 w-full">
                {files.map((file) => (
                  <div style={{ width: 100, height: 100 }} className="relative">
                    <img
                      src={file.url}
                      style={{ width: 100, height: 100 }}
                      alt={file.path}
                    />
                    <TrashIcon
                      className="absolute top-1 right-1 cursor-pointer text-red-400 hover:text-red-600 w-4"
                      onClick={() => {
                        setFiles((val) => {
                          return val.filter((f) => f.id !== file.id);
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="pt-4">
          <Button
            appearance="primary"
            onClick={() => {
              data.description = editorContent;
              data.images = files;
              onSubmit(data!);
              setData(
                promotion || {
                  id: "",
                  name: "",
                  description: "",
                }
              );
            }}
          >
            Submit
          </Button>
          <Button appearance="link" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default PromotionForm;
